/**
=========================================================
* Elite 247 Admin Panel - Modernized Bootcamp Applications
=========================================================
*/

import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import RecordTable from "components/RecordTable";

import { adminApi } from "services/api";
import PaymentLinkModal from "components/PaymentLinkModal";
import CallButton from "components/CallButton";
import { bootcampApplicationsToRows } from "utils/exportUtils";

const STATUS_FILTER_OPTIONS = ["pending", "approved", "rejected", "waitlisted", "cancelled"].map(
  (s) => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) })
);

const EXPORT_CONFIG = {
  fetchAll: adminApi.exportBootcampApplications,
  toRows: bootcampApplicationsToRows,
  filename: "bootcamp-applications",
  title: "Bootcamp Applications",
};

function BootcampApplications() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [paymentLinkTarget, setPaymentLinkTarget] = useState(null);

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedApplication(null);
  };

  const columns = [
    { Header: "name", accessor: "name", width: "20%", align: "left" },
    { Header: "email", accessor: "email", width: "25%", align: "left" },
    { Header: "bootcamp", accessor: "bootcamp", width: "25%", align: "left" },
    { Header: "status", accessor: "status", width: "15%", align: "center" },
    { Header: "date", accessor: "date", width: "15%", align: "left" },
    { Header: "actions", accessor: "actions", width: "10%", align: "center" },
  ];

  const renderRow = (application) => {
    // Some writers store upper-case statuses (APPROVED); treat them like the lower-case ones.
    const status = (application.status || "pending").toLowerCase();
    return {
      name: `${application.name}`,
      email: application.email,
      bootcamp: application.bootcamp?.title || "N/A",
      status: (
        <Tooltip title={`Status: ${application.status || "Pending"}`} placement="top">
          <MDBox ml={-1}>
            <Chip
              label={status}
              size="small"
              sx={{
                backgroundColor:
                  status === "approved" ? "#4caf50" : status === "rejected" ? "#f44336" : "#ff9800",
                color: "white",
                fontWeight: "bold",
                borderRadius: "8px",
                textTransform: "capitalize",
              }}
            />
          </MDBox>
        </Tooltip>
      ),
      date: new Date(application.createdAt).toLocaleDateString(),
      actions: (
        <MDBox display="flex" justifyContent="center" gap={0.5}>
          <Tooltip title="View application details" placement="top">
            <IconButton
              onClick={() => handleViewDetails(application)}
              sx={{
                color: "#667eea",
                "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.1)" },
                transition: "all 0.2s ease",
              }}
            >
              <Icon>visibility</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Create payment link" placement="top">
            <IconButton
              onClick={() => setPaymentLinkTarget(application)}
              sx={{
                color: "#00996a",
                "&:hover": { backgroundColor: "rgba(0, 150, 106, 0.1)" },
                transition: "all 0.2s ease",
              }}
            >
              <Icon>link</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    };
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card
              sx={{
                boxShadow: "0 8px 32px rgba(102, 126, 234, 0.15)",
                border: "1px solid rgba(102, 126, 234, 0.1)",
                transition: "all 0.3s ease",
              }}
            >
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={{ xs: 2, md: 3 }}
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "lg",
                  boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background:
                      "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                    pointerEvents: "none",
                  },
                }}
              >
                <MDBox
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  gap={{ xs: 2, md: 0 }}
                >
                  <MDBox display="flex" alignItems="center" gap={2}>
                    <MDBox
                      sx={{
                        width: "50px",
                        height: "50px",
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                      }}
                    >
                      <Icon sx={{ color: "white", fontSize: 28 }}>school</Icon>
                    </MDBox>
                    <MDBox>
                      <MDTypography variant="h5" fontWeight="bold" color="white">
                        Bootcamp Applications
                      </MDTypography>
                      <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                        Review and manage bootcamp enrollment requests
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                    <Chip
                      label="All applications"
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.2)",
                        color: "white",
                        fontWeight: "bold",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(255, 255, 255, 0.3)",
                      }}
                    />
                  </MDBox>
                </MDBox>
              </MDBox>

              <RecordTable
                columns={columns}
                fetchPage={adminApi.getBootcampApplications}
                renderRow={renderRow}
                statusOptions={STATUS_FILTER_OPTIONS}
                exportConfig={EXPORT_CONFIG}
                emptyMessage="No applications yet"
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{
          sx: {
            borderRadius: isPhone ? 0 : "16px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            fontWeight: "bold",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon>assignment_ind</Icon>
            <span>Application Details</span>
          </MDBox>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          {selectedApplication && (
            <>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Name:
                </MDTypography>
                <MDTypography variant="body2">{selectedApplication.name}</MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Email:
                </MDTypography>
                <MDTypography variant="body2">{selectedApplication.email}</MDTypography>
              </MDBox>
              {selectedApplication.phone && (
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Phone:
                  </MDTypography>
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDTypography variant="body2">{selectedApplication.phone}</MDTypography>
                    <CallButton phoneNumber={selectedApplication.phone} />
                  </MDBox>
                </MDBox>
              )}
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Bootcamp:
                </MDTypography>
                <MDTypography variant="body2">
                  {selectedApplication.bootcamp?.title || "N/A"}
                </MDTypography>
              </MDBox>
              {selectedApplication.education && (
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Education:
                  </MDTypography>
                  <MDTypography variant="body2">{selectedApplication.education}</MDTypography>
                </MDBox>
              )}
              {selectedApplication.experience && (
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Experience:
                  </MDTypography>
                  <MDTypography variant="body2">{selectedApplication.experience}</MDTypography>
                </MDBox>
              )}
              {selectedApplication.motivation && (
                <MDBox mb={2}>
                  <MDTypography variant="button" fontWeight="bold">
                    Motivation:
                  </MDTypography>
                  <MDTypography variant="body2">{selectedApplication.motivation}</MDTypography>
                </MDBox>
              )}
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Status:
                </MDTypography>
                <MDTypography variant="body2">
                  {selectedApplication.status || "Pending"}
                </MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDTypography variant="button" fontWeight="bold">
                  Applied On:
                </MDTypography>
                <MDTypography variant="body2">
                  {new Date(selectedApplication.createdAt).toLocaleString()}
                </MDTypography>
              </MDBox>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="info">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      <PaymentLinkModal
        open={!!paymentLinkTarget}
        onClose={() => setPaymentLinkTarget(null)}
        prefill={{
          name: paymentLinkTarget?.name || "",
          email: paymentLinkTarget?.email || "",
          phone: paymentLinkTarget?.phone || "",
          description: `Bootcamp enrolment – ${
            paymentLinkTarget?.bootcamp?.title || paymentLinkTarget?.name || ""
          }`,
        }}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default BootcampApplications;
